import React, { useState, useEffect, useRef } from "react";
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';

type Item = {
  [key: string]: any;
  selected?: boolean;
};

interface MultiSelectProps {
  /**
   * Items to display in the dropdown
   */
  items: Item[];
  
  /**
   * Key to use as the item's unique identifier
   */
  itemKey?: string;
  
  /**
   * Key to use as the item's display text
   */
  textKey?: string;

  /**
   * Optional secondary text key — renders a smaller sub-line under the option's
   * main text in the dropdown (e.g. a team's division label). Chips still show
   * only `textKey`. Also included in the search filter when present.
   */
  subTextKey?: string;

  /**
   * Label displayed above the component
   */
  label?: string;
  
  /**
   * Placeholder text when no items are selected
   */
  placeholder?: string;
  
  /**
   * Handler for when selected items change
   */
  onChange?: (selected: string[]) => void;
  
  /**
   * Whether to enable grouping items by section
   */
  showSectionHeaders?: boolean;
  
  /**
   * Whether to show the list of selected items
   */
  showSelectedItems?: boolean;
  
  /**
   * Whether to allow clearing all selections at once
   */
  canClear?: boolean;
  
  /**
   * Key to use for section grouping
   */
  sectionKey?: string;
  
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  items = [],
  itemKey = "id",
  textKey = "text",
  subTextKey,
  label,
  placeholder = "Select options",
  onChange,
  showSectionHeaders = false,
  showSelectedItems = true,
  canClear = false,
  sectionKey = "location",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize selected items from props
  useEffect(() => {
    const initialSelectedItems = items
      .filter((item) => item.selected)
      .map((item) => item[itemKey]);
    
    setSelectedItemIds(initialSelectedItems);
  }, [items, itemKey]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (itemId: string) => {
    let updatedSelection: string[];
    
    if (selectedItemIds.includes(itemId)) {
      updatedSelection = selectedItemIds.filter((id) => id !== itemId);
    } else {
      updatedSelection = [...selectedItemIds, itemId];
    }
    
    setSelectedItemIds(updatedSelection);
    if (onChange) {
      onChange(updatedSelection);
    }
  };

  const handleRemoveItem = (itemId: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    const updatedSelection = selectedItemIds.filter((id) => id !== itemId);
    setSelectedItemIds(updatedSelection);
    if (onChange) {
      onChange(updatedSelection);
    }
  };

  const handleClearAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedItemIds([]);
    if (onChange) {
      onChange([]);
    }
  };

  // Filter items based on search query
  const filteredItems = searchQuery.trim() === ""
    ? items
    : items.filter((item) => {
        const q = searchQuery.toLowerCase();
        const inText = String(item[textKey] ?? "").toLowerCase().includes(q);
        const inSub = subTextKey
          ? String(item[subTextKey] ?? "").toLowerCase().includes(q)
          : false;
        return inText || inSub;
      });

  // Group items by section if needed
  const itemsBySection: Record<string, Item[]> = {};
  
  if (showSectionHeaders && sectionKey) {
    filteredItems.forEach((item) => {
      const sectionName = item[sectionKey] || "Other";
      if (!itemsBySection[sectionName]) {
        itemsBySection[sectionName] = [];
      }
      itemsBySection[sectionName].push(item);
    });
  }

  // Get selected items data for display
  const selectedItems = items.filter((item) => 
    selectedItemIds.includes(item[itemKey])
  );

  return (
    <div className="w-full" ref={dropdownRef}>
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
          {label}
        </label>
      )}

      <div className="relative">
        {/* Selected Items Display / Main Input */}
        <div
          onClick={toggleDropdown}
          className={`flex min-h-[44px] w-full flex-wrap items-center gap-2 rounded-lg border border-gray-300 bg-white p-2 shadow-sm transition-all dark:border-gray-700 dark:bg-gray-900
            ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"} 
            ${isOpen ? "border-primary-500 ring-2 ring-primary-200 dark:border-primary-600 dark:ring-primary-900" : ""}
          `}
        >
          {showSelectedItems && selectedItems.length > 0 ? (
            <>
              {selectedItems.map((item) => (
                <div
                  key={item[itemKey]}
                  className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-sm dark:bg-gray-800"
                >
                  <span>{item[textKey]}</span>
                  <button
                    type="button"
                    onClick={(e) => handleRemoveItem(item[itemKey], e)}
                    className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <GoogleMaterialIcon name="close" size={14} />
                  </button>
                </div>
              ))}
            </>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">
              {placeholder}
            </span>
          )}

          <div className="ml-auto flex items-center space-x-1">
            {canClear && selectedItems.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <GoogleMaterialIcon name="close" size={18} />
              </button>
            )}
            <GoogleMaterialIcon name="keyboard_arrow_down" className={`h-5 w-5 text-gray-400 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`} />
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute left-0 z-50 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
            {/* Search Input */}
            <div className="border-b border-gray-200 p-2 dark:border-gray-700">
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <GoogleMaterialIcon name="search" className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Items List */}
            <div className="max-h-60 overflow-y-auto p-1">
              {showSectionHeaders && sectionKey
                ? // Render with section headers
                  Object.entries(itemsBySection).map(([section, sectionItems]) => (
                    <div key={section} className="mb-2">
                      <div className="mx-2 my-1 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                        {section}
                      </div>
                      {sectionItems.map((item) => (
                        <div
                          key={item[itemKey]}
                          className={`flex cursor-pointer items-center justify-between rounded px-2 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800
                            ${selectedItemIds.includes(item[itemKey]) ? "bg-primary-50 dark:bg-primary-900/20" : ""}
                          `}
                          onClick={() => handleSelect(item[itemKey])}
                        >
                          <div className="min-w-0"><div className="text-gray-900 dark:text-white">{item[textKey]}</div>{subTextKey && item[subTextKey] ? <div className="text-xs text-gray-500 dark:text-gray-400">{item[subTextKey]}</div> : null}</div>
                          {selectedItemIds.includes(item[itemKey]) && (
                            <GoogleMaterialIcon name="check" className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                : // Render flat list
                  filteredItems.map((item) => (
                    <div
                      key={item[itemKey]}
                      className={`flex cursor-pointer items-center justify-between rounded px-2 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800
                        ${selectedItemIds.includes(item[itemKey]) ? "bg-primary-50 dark:bg-primary-900/20" : ""}
                      `}
                      onClick={() => handleSelect(item[itemKey])}
                    >
                      <div className="min-w-0"><div className="text-gray-900 dark:text-white">{item[textKey]}</div>{subTextKey && item[subTextKey] ? <div className="text-xs text-gray-500 dark:text-gray-400">{item[subTextKey]}</div> : null}</div>
                      {selectedItemIds.includes(item[itemKey]) && (
                        <GoogleMaterialIcon name="check" className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                      )}
                    </div>
                  ))}

              {filteredItems.length === 0 && (
                <div className="px-3 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                  No results found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelect; 